export interface AccessControlledNavigationItem {
  route: readonly unknown[];
  children?: AccessControlledNavigationItem[];
}

export function getNavigationMenuId(item: AccessControlledNavigationItem): string {
  return item.route.map(String).join('/');
}

/**
 * True when menuId is itself an allowed ID, or is a nested local-menu route
 * beneath one (e.g. 'datasetup/payroll-setup/general' under the allowed
 * 'datasetup/payroll-setup'). Shared by nav filtering and the route guard so
 * both sides of "hide the link" / "block the URL" agree on the same rule.
 */
export function isMenuIdAllowed(menuId: string, allowedMenuIds: readonly string[]): boolean {
  return allowedMenuIds.some(
    (allowedId) => menuId === allowedId || menuId.startsWith(`${allowedId}/`)
  );
}

/**
 * Filters a navigation tree using the route-based IDs stored by the admin app.
 * Parent containers are retained whenever they contain an allowed descendant.
 * An allowed parent route also grants its nested local-menu routes.
 */
export function filterNavigationItems<T extends AccessControlledNavigationItem>(
  items: readonly T[],
  allowedMenuIds: readonly string[],
  resolveMenuId: (item: T) => string = getNavigationMenuId
): T[] {
  return items.flatMap((item) => {
    const filteredChildren = item.children
      ? filterNavigationItems(item.children as unknown as readonly T[], allowedMenuIds, resolveMenuId)
      : [];
    const menuId = resolveMenuId(item);
    const isAllowed = isMenuIdAllowed(menuId, allowedMenuIds);

    if (!isAllowed && filteredChildren.length === 0) {
      return [];
    }

    return [{
      ...item,
      ...(item.children ? { children: filteredChildren } : {}),
    } as T];
  });
}

export function getFirstAllowedNavigationRoute<T extends AccessControlledNavigationItem>(
  items: readonly T[]
): string[] | null {
  for (const item of items) {
    if (item.children?.length) {
      const childRoute = getFirstAllowedNavigationRoute(item.children as unknown as readonly T[]);
      if (childRoute) return childRoute;
    } else {
      return item.route.map(String);
    }
  }

  return null;
}
